import {
    Component,
    DestroyRef,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { UserInfo } from '../../services/user.service';
import { AppService } from '../../services/app.service';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { UserService } from '../../services/user.service';
import { AppSubmissionTable } from '../../classes/application-submission-table';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { DateTime } from 'luxon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SchoolYearPipe } from '../../pipes/schoolyear/schoolyear.pipe';
import { MatButtonModule } from '@angular/material/button';
import { ErrorService } from '../../services/error.service';

@Component({
    selector: 'app-submitted',
    templateUrl: './submitted.component.html',
    imports: [
        MatDividerModule,
        MatFormFieldModule,
        MatInputModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        CommonModule,
        FormsModule,
        SchoolYearPipe,
        MatButtonModule
    ]
})
export class SubmittedComponent implements OnInit {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly route = inject(ActivatedRoute);
    protected readonly confirmDlgSvc = inject(ConfirmationDialogService);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly userSvc = inject(UserService);
    protected readonly errorSvc = inject(ErrorService);
    user: UserInfo;
    dataSource: MatTableDataSource<AppSubmissionTable>;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    displayedColumns: string[] = [
        'AppStartDate',
        'StudentName',
        'School',
        'LastSavedDate',
        'FinalizedDate',
    ];
    pageSize: number;
    pageIndex: number;
    schoolYear: string;
    submissionDeadline: string;
    submitted: boolean;
    input: string;

    ngOnInit() {
        this.pageSize = parseInt(sessionStorage.getItem('pageSizeSub')) || 10;
        this.pageIndex = parseInt(sessionStorage.getItem('pageIndexSub')) || 0;
        this.input = sessionStorage.getItem('filterSub') || '';

        this.route.params
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((params) => {
                this.submitted = params['submitted'] === 'true' ? true : false;
                this.user = this.userSvc.User;
                this.getTableData();
                this.pageSize =
                    parseInt(sessionStorage.getItem('pageSizeSub')) || 10;
            });
    }

    getTableData() {
        this.appSvc
            .GetSubmittedTable(this.submitted)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.dataSource = new MatTableDataSource(res.TableData);
                    this.dataSource.paginator = this.paginator;
                    this.schoolYear = res.SchoolYear;
                    this.submissionDeadline = res.SubmissionDeadline;
                    this.dataSource.filter = this.input;
                    let sortActive = sessionStorage.getItem('activeSub') || '';
                    let sortDirection: SortDirection = sessionStorage.getItem('directionSub') as SortDirection || '';
                    this.sortChange({ active: sortActive, direction: sortDirection });
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    sendEmails() {
        let dialog = this.confirmDlgSvc.Open({
            disableClose: true,
            TitleText: 'Are you sure?',
            MessageText:
                'Send reminder emails to all students with unsubmitted applications',
            OKText: 'Send',
            CancelText: 'Cancel',
        });

        dialog.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
            if (res === true) {
                this.appSvc
                    .SendReminderEmails()
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (res) => {
                            this.snackBarSvc.success(
                                'Emails Sent',
                                10000
                            );
                        },
                        error: (error) => {
                            this.errorSvc.HandleServiceError(error);
                        }
                    });
            }
        });
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
        sessionStorage.setItem('filterSub', filterValue.trim().toLowerCase());

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    handlePageEvent(e: PageEvent) {
        this.pageSize = e.pageSize;
        sessionStorage.setItem('pageSizeSub', e.pageSize.toString());
        this.pageIndex = e.pageIndex;
        sessionStorage.setItem('pageIndexSub', e.pageIndex.toString());
    }

    sortChange(sort: Sort) {
        if (!sort.active || sort.direction === '') {
            this.dataSource.data = this.dataSource.data.slice();
            return;
        }

        sessionStorage.setItem('activeSub', sort.active);
        sessionStorage.setItem('directionSub', sort.direction);

        this.dataSource.data = this.dataSource.data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'AppStartDate':
                    return compare(a.AppStartDate == null ? 0 : DateTime.fromISO(a.AppStartDate).toMillis(), b.AppStartDate == null ? 0 : DateTime.fromISO(b.AppStartDate).toMillis(), isAsc);
                case 'StudentName':
                    return compare(a.StudentName.toLowerCase(), b.StudentName.toLowerCase(), isAsc);
                case 'School':
                    return compare(a.School.toLowerCase(), b.School.toLowerCase(), isAsc);
                case 'LastSavedDate':
                    return compare(a.LastSavedDate == null ? 0 : DateTime.fromISO(a.LastSavedDate).toMillis(), b.LastSavedDate == null ? 0 : DateTime.fromISO(b.LastSavedDate).toMillis(), isAsc);
                case 'FinalizedDate':
                    return compare(a.FinalizedDate == null ? 0 : DateTime.fromISO(a.FinalizedDate).toMillis(), b.FinalizedDate == null ? 0 : DateTime.fromISO(b.FinalizedDate).toMillis(), isAsc);
                default:
                    return 0;
            }
        });
    }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
